import React from "react";
import { Workflow, AlertTriangle } from "lucide-react";
import { Modal } from "./Modal";
import { SubcontractorTypeahead } from "./SubcontractorTypeahead";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

// Same fixed list the Receiving intake form uses for "Sub Con reason", plus
// "Repair" — covers a defective/closed item being sent out again as a
// replacement sample at the customer's request.
export const SUBCON_RETURN_REASONS = ["No In-House Capability", "SANAS", "Repair"];

/**
 * Manager/Admin AND Dispatch staff. Sends a CLOSED/PAID line back into the
 * subcontractor workflow (status -> "awaiting_subcontractor") — e.g. a
 * defective item already dispatched needs a replacement sample sent out to
 * a subcontractor at the customer's request. From here it follows the exact
 * same flow as any normal subcontractor item (Subcontractor page: To send ->
 * Send to Subcon -> Received -> Ready for Dispatch).
 */
export function ReturnToSubconModal({ item, onClose, onDone }) {
  const [subcontractorName, setSubcontractorName] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const valid = subcontractorName.trim() && reason && note.trim();

  const confirm = async () => {
    if (!valid) { toast.error("Subcontractor, reason and a note are all required."); return; }
    setBusy(true);
    try {
      await api.patch(`/job-items/${item.id}/status`, {
        status: "awaiting_subcontractor",
        subcontractor_name: subcontractorName.trim(),
        reason_for_subcontracting: reason,
        notes: note.trim(),
      });
      toast.success("Line sent back into the Subcontractor queue");
      onDone && onDone();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Workflow}
      title="Send back to Subcontractor"
      subtitle={`${item.description || ""} · ${item.serial_number ? "SN " + item.serial_number : item.issue_number ? "Issue " + item.issue_number : "—"} · currently ${item.status === "paid" ? "Paid" : "Closed"}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="return-subcon-cancel">Cancel</button>
        <button className="btn gold" onClick={confirm} disabled={!valid || busy} data-testid="return-subcon-confirm">
          <Workflow size={13} /> {busy ? "Sending…" : "Send back to Subcontractor"}
        </button>
      </>}>

      <div className="notice" style={{ marginBottom: 12 }}>
        <AlertTriangle size={14} />
        <span>
          This moves only this ONE line into "To send" on the Subcontractor page — every other line
          on this DN stays exactly as it is. Use this when a replacement sample needs to go out
          after the original item was already closed/dispatched.
        </span>
      </div>

      <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
        Subcontractor *
      </label>
      <SubcontractorTypeahead
        value={subcontractorName} onChange={setSubcontractorName}
        placeholder="Start typing — pick from history or enter new"
        testid="return-subcon-name" />
      <div style={{ marginBottom: 12 }} />

      <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
        Reason *
      </label>
      <select className="input" value={reason} onChange={(e) => setReason(e.target.value)}
        data-testid="return-subcon-reason" style={{ width: "100%", marginBottom: 12 }}>
        <option value="">Select a reason…</option>
        {SUBCON_RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>

      <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
        Note (required — kept in the line's status history for audit) *
      </label>
      <input type="text" placeholder="e.g. Customer requested a replacement sample after the defective unit"
        value={note} onChange={(e) => setNote(e.target.value)}
        data-testid="return-subcon-note"
        style={{ width: "100%" }} />
    </Modal>
  );
}
